/**
 * True Strength Index (TSI)
 *
 * Double-smoothed momentum oscillator using two EMA periods.
 * TSI = 100 × EMA(EMA(momentum, long), short) / EMA(EMA(|momentum|, long), short)
 * Signal line = EMA(TSI, signalPeriod).
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const tsiIndicator: IndicatorDefinition = {
  id: "tsi",
  name: "True Strength Index",
  shortName: "TSI",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "longPeriod", label: "Long Period", type: "number", default: 25, min: 2, max: 200 },
    { key: "shortPeriod", label: "Short Period", type: "number", default: 13, min: 2, max: 100 },
    { key: "signalPeriod", label: "Signal Period", type: "number", default: 13, min: 2, max: 100 },
  ],
  plots: [
    { key: "tsi", label: "TSI", color: "#2196F3", style: "line", lineWidth: 2, priceScaleId: "tsi" },
    { key: "signal", label: "Signal", color: "#FF9800", style: "line", lineWidth: 1, priceScaleId: "tsi" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { longPeriod, shortPeriod, signalPeriod } = params;

    if (candles.length < longPeriod + shortPeriod + 2)
      return [{ key: "tsi", data: [] }, { key: "signal", data: [] }];

    // Price momentum
    const momentum: number[] = [];
    for (let i = 1; i < candles.length; i++) {
      momentum.push(candles[i].close - candles[i - 1].close);
    }
    const absMomentum = momentum.map(Math.abs);

    // Double EMA helper
    const ema = (arr: number[], period: number): number[] => {
      const result: number[] = [];
      const k = 2 / (period + 1);
      let e = NaN;
      for (let i = 0; i < arr.length; i++) {
        if (i < period - 1) { result.push(NaN); continue; }
        if (i === period - 1) {
          e = 0;
          for (let j = 0; j < period; j++) e += arr[j];
          e /= period;
        } else {
          e = arr[i] * k + e * (1 - k);
        }
        result.push(e);
      }
      return result;
    };

    const ema1 = ema(momentum, longPeriod);
    const ema1abs = ema(absMomentum, longPeriod);

    // Filter out NaN for second EMA
    const validEma1: number[] = [];
    const validEma1abs: number[] = [];
    const validIdx: number[] = [];
    for (let i = 0; i < ema1.length; i++) {
      if (!isNaN(ema1[i])) {
        validEma1.push(ema1[i]);
        validEma1abs.push(ema1abs[i]);
        validIdx.push(i);
      }
    }

    const ema2 = ema(validEma1, shortPeriod);
    const ema2abs = ema(validEma1abs, shortPeriod);

    const tsiValues: { time: number; value: number }[] = [];
    const tsiRaw: number[] = [];
    for (let i = 0; i < ema2.length; i++) {
      if (!isNaN(ema2[i]) && ema2abs[i] !== 0) {
        const val = 100 * (ema2[i] / ema2abs[i]);
        tsiRaw.push(val);
        tsiValues.push({ time: candles[validIdx[i] + 1].time, value: val });
      }
    }

    // Signal = EMA of TSI
    const signalData: { time: number; value: number }[] = [];
    if (tsiRaw.length >= signalPeriod) {
      const sigEma = ema(tsiRaw, signalPeriod);
      let outIdx = 0;
      for (let i = 0; i < sigEma.length; i++) {
        if (!isNaN(sigEma[i]) && outIdx < tsiValues.length) {
          signalData.push({ time: tsiValues[i].time, value: sigEma[i] });
        }
        outIdx++;
      }
    }

    return [{ key: "tsi", data: tsiValues }, { key: "signal", data: signalData }];
  },
};
